import React, { useState, useEffect } from 'react';
import { XMarkIcon, CameraIcon, ArrowRightIcon } from './icons';
import { ImageUploader } from './ImageUploader';
import { ImageViewer } from './ImageViewer';
import { ControlPanel } from './ControlPanel';
import { downscaleImage } from '../utils/watermarkUtils';
import { generateHeadshot } from '../services/geminiService';
import { OUTFIT_OPTIONS, POSE_OPTIONS } from '../constants';
import { TEMPLATE_CATEGORIES, getTemplateById } from '../constants/templates';
import { TemplatePreview } from './TemplatePreview';

interface TrialModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgrade: () => void;
  userPlan: UserPlan | null;
  onTrialUsed: () => void;
  isPaidUser?: boolean;
  scrollToPlanInfo?: () => void;
}

type TrialStep = 'upload' | 'template' | 'generate' | 'result';

export const TrialModal: React.FC<TrialModalProps> = ({ isOpen, onClose, onUpgrade, userPlan, onTrialUsed, isPaidUser = false, scrollToPlanInfo }) => {
  const [currentStep, setCurrentStep] = useState<TrialStep>('upload');
  const [originalImage, setOriginalImage] = useState<{ data: string; mimeType: string } | null>(null);
  const [generatedImage, setGeneratedImage] = useState<string | null>(null);
  const [downscaledImage, setDownscaledImage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPrompt, setCurrentPrompt] = useState<string>('');
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState('');
  
  // 無料体験の残り回数を計算
  const remainingTrials = userPlan?.creditsRest || 0;
  
  // テンプレート選択用の状態
  const [selectedTemplate, setSelectedTemplate] = useState('studio_white');
  const [selectedOutfit, setSelectedOutfit] = useState('suit_blue');
  const [selectedPose, setSelectedPose] = useState('front');
  
  // テンプレート一覧を取得（ビジネスカテゴリのみ）
  const templates = TEMPLATE_CATEGORIES.find(cat => cat.id === 'business')?.templates || [];
  const [customOutfitText, setCustomOutfitText] = useState('');
  const [customTemplateText, setCustomTemplateText] = useState('');

  // 無料版: 保存・DL用に低解像度（50%）版を生成
  useEffect(() => {
    if (generatedImage) {
      downscaleImage(generatedImage, 0.5)
        .then(setDownscaledImage)
        .catch(() => setDownscaledImage(generatedImage));
    }
  }, [generatedImage]);

  // 画像生成中の進行状況を更新
  useEffect(() => {
    if (!isLoading) {
      return;
    }

    const steps = [
      { progress: 20, text: '画像を分析中...' },
      { progress: 40, text: 'AIが生成中...' },
      { progress: 60, text: '画像を処理中...' },
      { progress: 80, text: '最終調整中...' },
      { progress: 95, text: '仕上げ中...' }
    ];

    let currentStepIndex = 0;
    let maxProgress = 0;
    
    const interval = setInterval(() => {
      if (currentStepIndex < steps.length) {
        const progress = steps[currentStepIndex].progress;
        setGenerationProgress(progress);
        setGenerationStep(steps[currentStepIndex].text);
        maxProgress = Math.max(maxProgress, progress);
        currentStepIndex++;
      } else {
        // 最後のステップで95%に留める（ただし、タイムアウトを防ぐため最大90秒で100%に戻す）
        if (maxProgress < 95) {
          setGenerationProgress(95);
          maxProgress = 95;
        }
      }
    }, 4000); // 4秒ごとに更新

    // モバイル環境でのタイムアウト保護（90秒後に100%に戻してエラーを表示）
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    const timeoutMs = isMobile ? 90000 : 60000;
    
    const timeoutId = setTimeout(() => {
      console.warn('⚠️ 画像生成がタイムアウトしました（プログレスバー保護）');
      setGenerationProgress(100);
      setGenerationStep('タイムアウト...');
    }, timeoutMs);

    return () => {
      clearInterval(interval);
      clearTimeout(timeoutId);
    };
  }, [isLoading]);

  // テンプレートカテゴリの状態を追加
  const [selectedCategory, setSelectedCategory] = useState('business');

  const outfits = [
    { id: 'suit_blue', name: 'ビジネススーツ (ネイビー)', desc: '信頼感のあるネイビーのスーツ', isPremium: false },
    { id: 'suit_black', name: 'ビジネススーツ (ブラック)', desc: '上品なブラックのスーツ', isPremium: false },
    { id: 'suit_gray', name: 'ビジネススーツ (グレー)', desc: '落ち着いたグレーのスーツ', isPremium: false },
    { id: 'blouse', name: 'ブラウス', desc: '清潔感のある白いブラウス', isPremium: false },
    { id: 'custom_outfit', name: 'その他（テキストで指定）', desc: 'カスタム衣装', isPremium: false },
    { id: 'custom_outfit_image', name: '⭐️その他（衣装を添付する）', desc: 'オリジナル衣装を添付', isPremium: true }
  ];

  const handleImageUpload = (base64Image: string, mimeType: string) => {
    setOriginalImage({ data: base64Image, mimeType });
    setCurrentStep('template');
    setError(null);
  };

  const handleGenerate = async () => {
    if (!originalImage) return;

    // 無料体験の制限チェック - 制限に達した場合は課金に移行
    if (remainingTrials <= 0) {
      handleUpgrade();
      return;
    }

    setIsLoading(true);
    setError(null);
    setGeneratedImage(null);
    setCurrentStep('generate');
    setGenerationProgress(0);
    setGenerationStep('画像を分析中...');

    try {
      // 最適化されたプロンプトを生成（短く、具体的に）
      let prompt = "プロのポートレート写真。85mm、F2.8、自然なボケ。柔らかい照明、キャッチライト。自然なメイク、健康的な肌。背筋を伸ばし、軽い微笑みでカメラ目線。高品質、自然な仕上がり。";
      
      // 背景設定（簡潔に）
      if (selectedTemplate === 'custom_template' && customTemplateText) {
        prompt += ` 背景: ${customTemplateText}`;
      } else {
        const template = templates.find(t => t.id === selectedTemplate);
        if (template && template.prompt) {
          // プロンプトを簡潔に要約
          if (template.prompt.includes('白いスタジオ')) {
            prompt += " 背景: 白いスタジオ";
          } else if (template.prompt.includes('グレーのスタジオ')) {
            prompt += " 背景: グレーのスタジオ";
          } else if (template.prompt.includes('ダークグレー')) {
            prompt += " 背景: ダークグレーのスタジオ";
          } else {
            prompt += " 背景: プロフェッショナルなスタジオ";
          }
        } else {
          prompt += " 背景: 白いスタジオ";
        }
      }

      // 衣装設定（簡潔に）
      if (selectedOutfit === 'custom_outfit' && customOutfitText) {
        prompt += ` 衣装: ${customOutfitText}`;
      } else {
        const outfit = outfits.find(o => o.id === selectedOutfit);
        if (outfit) {
          switch(outfit.id) {
            case 'suit_blue':
              prompt += " 衣装: ネイビーのビジネススーツ、白いシャツ";
              break;
            case 'suit_black':
              prompt += " 衣装: ブラックのビジネススーツ、白いシャツ";
              break;
            case 'suit_gray':
              prompt += " 衣装: グレーのビジネススーツ、白いシャツ";
              break;
            case 'blouse':
              prompt += " 衣装: 白いブラウス";
              break;
            default:
              prompt += " 衣装: ビジネススーツ";
              break;
          }
        }
      }

      // ポーズ設定
      console.log('🔍 TrialModal - ポーズ設定デバッグ:', {
        selectedPose,
        poseOptionsLength: POSE_OPTIONS.length
      });
      
      const poseOption = POSE_OPTIONS.find(p => p.id === selectedPose);
      console.log('🔍 TrialModal - 選択されたポーズ:', poseOption);
      
      if (poseOption) {
        prompt += ` ${poseOption.prompt}`;
      }

      // デバッグ用：最終プロンプトをコンソールに出力
      console.log('無料版 - 最終プロンプト:', prompt);
      console.log('無料版 - 選択されたテンプレート:', selectedTemplate);
      console.log('無料版 - テンプレート詳細:', templates.find(t => t.id === selectedTemplate));
      
      // プロンプトを保存して表示用に使用
      setCurrentPrompt(prompt);
      
      // デバッグ：画像生成前の状態確認
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      console.log('🔍 TrialModal - generateHeadshot呼び出し前:', {
        isMobile,
        hasOriginalImage: !!originalImage,
        originalImageDataLength: originalImage.data?.length,
        originalImageDataPrefix: originalImage.data?.substring(0, 50),
        mimeType: originalImage.mimeType,
        promptLength: prompt.length
      });
      
      const result = await generateHeadshot(
        originalImage.data,
        originalImage.mimeType,
        prompt,
        null,  // customOutfitImage
        null   // customBackgroundImage
      );
      
      setGeneratedImage(result);
      setCurrentStep('result');
      onTrialUsed(); // App.tsxの使用回数更新関数を呼び出し
    } catch (e: any) {
      console.error('無料版画像生成エラー:', e);
      console.error('エラー詳細:', {
        message: e.message,
        code: e.code,
        details: e.details,
        stack: e.stack,
        name: e.name
      });

      // Firebase Functionsからのエラーメッセージを解析
      let displayMessage = e.message || '画像生成中にエラーが発生しました。';
      let suggestion = '';

      // タイムアウトエラーの特別処理
      if (e.message?.includes('タイムアウト') || e.message?.includes('timeout') || e.code === 'deadline-exceeded') {
        displayMessage = '⏱️ 画像生成に時間がかかりすぎました。';
        suggestion = '\n\n💡 ヒント: ネットワーク接続を確認し、しばらく待ってから再度お試しください。モバイル環境では時間がかかる場合があります。';
      }

      // エラー詳細から理由と提案を取得
      if (e.details) {
        const reason = e.details.reason;
        const errorSuggestion = e.details.suggestion;

        if (reason === 'timeout' && !suggestion) {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'invalid_prompt') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'generation_failed') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'file_too_large') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (reason === 'api_limit') {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        } else if (errorSuggestion && !suggestion) {
          suggestion = '\n\n💡 ヒント: ' + errorSuggestion;
        }
      }

      // エラーコードに基づく追加処理
      if (e.code === 'deadline-exceeded' && !displayMessage.includes('タイムアウト')) {
        displayMessage = '⏱️ ' + displayMessage;
      } else if (e.code === 'invalid-argument') {
        displayMessage = '⚠️ ' + displayMessage;
      } else if (e.code === 'resource-exhausted') {
        displayMessage = '🚫 ' + displayMessage;
      } else if (e.code === 'internal') {
        displayMessage = '❌ ' + displayMessage;
      }

      setError(displayMessage + suggestion);
      setCurrentStep('template');
    } finally {
      setIsLoading(false);
      setGenerationProgress(0);
      setGenerationStep('');
    }
  };

  const handleTryAnother = () => {
    setCurrentStep('template');
    setGeneratedImage(null);
    setError(null);
  };

  const handleUpgrade = () => {
    onUpgrade();
    onClose();
  };


  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        {/* ヘッダー */}
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center space-x-4">
            <h2 className="text-xl font-bold text-slate-900">無料体験</h2>
            <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
              残り {remainingTrials} 回
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600"
          >
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* ステップインジケーター */}
        <div className="px-6 py-4 bg-slate-50">
          <div className="flex items-center justify-center space-x-4">
            {[
              { step: 'upload', label: '写真を選ぶ' },
              { step: 'template', label: 'テンプレート選択' },
              { step: 'generate', label: '生成' },
              { step: 'result', label: '結果' }
            ].map((item, index) => (
              <div key={item.step} className="flex items-center">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                    currentStep === item.step
                      ? 'bg-blue-600 text-white'
                      : index < ['upload', 'template', 'generate', 'result'].indexOf(currentStep)
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-200 text-slate-600'
                  }`}
                >
                  {index + 1}
                </div>
                <span className="ml-2 text-sm font-medium text-slate-600">
                  {item.label}
                </span>
                {index < 3 && (
                  <ArrowRightIcon className="w-4 h-4 text-slate-400 mx-4" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* コンテンツ */}
        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {currentStep === 'upload' && (
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                ステップ1: 写真を選ぶ
              </h3>
              <p className="text-slate-600 mb-8">
                顔がはっきり写った正面の写真をアップロードしてください
              </p>
              <ImageUploader onImageUpload={handleImageUpload} scrollToPlanInfo={scrollToPlanInfo} />
            </div>
          )}

          {currentStep === 'template' && (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-2xl font-bold text-slate-900">
                  ステップ2: テンプレート選択
                </h3>
                <button
                  onClick={() => setCurrentStep('upload')}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  戻る
                </button>
              </div>
              <p className="text-slate-600 mb-8">
                お好みのテンプレートと衣装を選択してください
              </p>
              
              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  背景テンプレート
                </label>
                <select
                  value={selectedTemplate}
                  onChange={(e) => {
                    const templateId = e.target.value;
                    console.log('Template selected:', templateId);
                    setSelectedTemplate(templateId);
                  }}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {templates && templates.length > 0 ? templates.map((template) => {
                    if (!template) return null;
                    return (
                      <option key={template.id} value={template.id}>
                        {template.name} {template.isPremium && !isPaidUser ? '(Pro プラン)' : ''}
                      </option>
                    );
                  }) : (
                    <option value="">テンプレートを読み込み中...</option>
                  )}
                  <option value="custom_template">その他（テキストで指定）</option>
                </select>
                {selectedTemplate && selectedTemplate !== 'custom_template' && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      {templates.find(t => t?.id === selectedTemplate)?.description || '説明なし'}
                    </p>
                  </div>
                )}
              </div>
              
              {selectedTemplate === 'custom_template' && (
                <div className="mb-8">
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    カスタム背景
                  </label>
                  <textarea
                    rows={3}
                    className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="例: モダンな建築の図書館、背景はぼかす"
                    value={customTemplateText}
                    onChange={(e) => setCustomTemplateText(e.target.value)}
                  />
                </div>
              )}

              <div className="mb-8">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  衣装
                </label>
                <select
                  value={selectedOutfit}
                  onChange={(e) => {
                    const outfitId = e.target.value;
                    console.log('Outfit selected:', outfitId);
                    const outfit = outfits.find(o => o.id === outfitId);
                    if (outfit && outfit.isPremium && !isPaidUser) {
                      handleUpgrade();
                    } else {
                      setSelectedOutfit(outfitId);
                    }
                  }}
                  className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  {outfits.map((outfit) => (
                    <option key={outfit.id} value={outfit.id}>
                      {outfit.name} {outfit.isPremium && !isPaidUser ? '(Pro プラン)' : ''}
                    </option>
                  ))}
                </select>
                {selectedOutfit && (
                  <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                    <p className="text-sm text-slate-600">
                      {outfits.find(o => o.id === selectedOutfit)?.desc || '説明なし'}
                    </p>
                  </div>
                )}
                
                {selectedOutfit === 'custom_outfit' && (
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      カスタム衣装
                    </label>
                    <textarea
                      rows={3}
                      className="w-full p-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="例: 上質な黒のタートルネックセーター"
                      value={customOutfitText}
                      onChange={(e) => setCustomOutfitText(e.target.value)}
                    />
                  </div>
                )}
              </div>

              {remainingTrials <= 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                  <p className="text-blue-800 text-sm">
                    <strong>無料体験の制限に達しました。有料プランにアップグレードしてください。</strong>
                  </p>
                  {scrollToPlanInfo && (
                    <button
                      onClick={scrollToPlanInfo}
                      className="text-sm text-blue-600 hover:text-blue-800 underline mt-2 block"
                    >
                      → プラン情報を見る
                    </button>
                  )}
                </div>
              )}

              <button
                onClick={handleGenerate}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-colors ${
                  remainingTrials <= 0
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                {remainingTrials <= 0 ? '有料プランにアップグレード' : '生成開始'}
              </button>
            </div>
          )}

          {currentStep === 'generate' && (
            <div className="text-center">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                生成中...
              </h3>
              <div className="bg-slate-100 rounded-lg p-8">
                <CameraIcon className="w-16 h-16 text-blue-600 mx-auto mb-4 animate-pulse" />
                
                {/* 進行状況表示 */}
                <div className="mb-6">
                  <p className="text-lg font-semibold text-slate-700 mb-2">{generationStep || 'AIが宣材写真を生成しています'}</p>
                  <div className="w-full bg-slate-200 rounded-full h-3 mb-2">
                    <div 
                      className="bg-blue-600 h-3 rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${generationProgress}%` }}
                    ></div>
                  </div>
                  <p className="text-sm text-slate-500">{generationProgress}%</p>
                </div>
                
                {/* プロンプト表示エリア */}
                {currentPrompt && (
                  <div className="mt-6 bg-white rounded-lg p-4 border">
                    <h4 className="text-sm font-medium text-slate-700 mb-2">送信中のプロンプト:</h4>
                    <div className="bg-slate-50 rounded p-3 max-h-32 overflow-y-auto">
                      <p className="text-xs text-slate-600 whitespace-pre-wrap leading-relaxed">
                        {currentPrompt}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {currentStep === 'result' && generatedImage && (
            <div>
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                生成完了！
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div>
                  <h4 className="font-semibold mb-2">元画像</h4>
                  <img
                    src={originalImage?.data}
                    alt="Original"
                    className="w-full aspect-square object-cover rounded-lg border"
                  />
                </div>
                <div className="relative">
                  <h4 className="font-semibold mb-2">生成画像（低解像度）</h4>
                  <div className="relative w-full aspect-square rounded-lg border overflow-hidden">
                    <img
                      src={downscaledImage || generatedImage}
                      alt="Generated"
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <p className="text-blue-800 text-sm">
                  <strong>無料体験版:</strong> 保存・ダウンロード時は低解像度になります。
                  高解像度で保存するには有料プランにアップグレードしてください。
                </p>
              </div>

              <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-6">
                <p className="text-slate-600 text-sm">
                  📱 <strong>スマホなどの場合:</strong> 画像を長押ししてダウンロード
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <button
                  onClick={handleUpgrade}
                  className="flex-1 bg-blue-600 text-white py-3 px-6 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  高解像度で保存（有料プランにアップグレード）
                </button>
                <button
                  onClick={remainingTrials <= 0 ? handleUpgrade : handleTryAnother}
                  className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-colors ${
                    remainingTrials <= 0
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {remainingTrials <= 0 ? '有料プランにアップグレード' : '別パターンを試す（消費1）'}
                </button>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4">
              <div className="flex items-start">
                <span className="text-red-600 text-2xl mr-3 flex-shrink-0">⚠️</span>
                <div className="flex-1">
                  <h3 className="text-red-800 font-semibold mb-2">エラーが発生しました</h3>
                  <p className="text-red-700 text-sm whitespace-pre-line">{error}</p>
                  <button
                    onClick={() => setError(null)}
                    className="mt-3 text-red-600 text-sm underline hover:text-red-800 transition-colors"
                  >
                    閉じる
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
